// src/admin/components/AdminModal.jsx
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';
import { useModalLayer } from '../../components/ui/modalStack.js';
import { useContentInset } from '../../components/ui/modalArea.js';

// `role` is OPTIONAL and additive: a caller that passes 'dialog' / 'alertdialog'
// gets the panel marked as such (aria-modal + aria-labelledby on the title);
// every existing caller passes nothing and renders exactly as before.
// v196: the close × is `type="button"` — the modal does not portal, so a dialog rendered inside a
// <form> (the personalization form's document picker) would otherwise SUBMIT that form on close.
// v198: Escape is honoured only by the TOP-most open modal (components/ui/modalStack.js) — a nested
// ConfirmDialog no longer closes the modal behind it on the same keystroke.
// v206: closeOnOverlay={false} keeps a dialog open on an overlay click (TypedConfirmDialog: a half-typed
// confirmation must not vanish); Escape and the × still close it.
export default function AdminModal({ open, onClose, title, children, width = 480, role, closeOnOverlay = true }) {
  const titleId = role ? 'admin-modal-title' : undefined;
  const isTop = useModalLayer(open);
  // Centred in the page's content area (beside the sidebar), not in the whole window — components/ui/modalArea.js.
  const insetLeft = useContentInset(open);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape' && isTop()) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, isTop]);

  if (!open) return null;

  return (
    <div
      onClick={closeOnOverlay ? onClose : undefined}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT_SANS,
        ...(insetLeft ? { paddingLeft: `${insetLeft}px` } : null),
      }}
      data-testid="admin-modal-overlay"
    >
      <div
        onClick={e => e.stopPropagation()}
        role={role}
        aria-modal={role ? 'true' : undefined}
        aria-labelledby={titleId}
        style={{
          background: COLORS.surface, borderRadius: RADII.control,
          width: '100%', maxWidth: width, maxHeight: '90vh',
          overflow: 'auto', padding: '24px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 id={titleId} style={{ fontSize: '16px', fontWeight: 700, color: COLORS.ink, margin: 0 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textSecondary, padding: '4px' }}
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
